import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api, NotSignedIn } from "./api.js";
import type { Me, Person } from "./types.js";

interface SessionValue {
  me: Me;
  /** Household timezone. Every date in the interface is rendered in it. */
  zone: string;
  /**
   * Whether this person may change the calendar. Hiding what someone cannot
   * do beats offering it and refusing — but the server refuses regardless,
   * and this is only how the interface stays honest about it.
   */
  canEdit: boolean;
  people: Person[];
  personById: (id: string) => Person | undefined;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Context = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(Context);
  if (value === null) throw new Error("useSession outside a SessionProvider");
  return value;
}

type State = { status: "loading" } | { status: "out" } | { status: "in"; me: Me };

export function SessionGate({
  children,
  signedOut,
}: {
  children: React.ReactNode;
  signedOut: React.ReactNode;
}): React.JSX.Element {
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    try {
      setState({ status: "in", me: await api.me() });
    } catch (error) {
      if (error instanceof NotSignedIn) setState({ status: "out" });
      else throw error;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<SessionValue | null>(() => {
    if (state.status !== "in") return null;
    const people = state.me.people;

    return {
      me: state.me,
      zone: state.me.household?.timezone ?? "UTC",
      canEdit: state.me.user.can_edit,
      people,
      personById: (id) => people.find((p) => p.id === id),
      refresh: load,
      signOut: async () => {
        await api.logout();
        setState({ status: "out" });
      },
    };
  }, [state, load]);

  // A blank frame rather than a spinner: this resolves in milliseconds from a
  // warm cache, and a flashed spinner reads as slower than nothing.
  if (state.status === "loading") return <div className="boot" />;
  if (state.status === "out" || value === null) return <>{signedOut}</>;

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
